import React, { useState, useEffect, useRef } from "react";
import listingPlaceholder from "../../../assets/ComponentsImages/listings/listing-placeholder.png";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import apiHelper from "../../../utils/apiHelper";
import RightSideModal from "../../Modal/right-side-modal";
import Card from "../../card";
import { FormProvider, useFormContext, useWatch } from "react-hook-form";
import InputField from "../../form-fields/InputField";
import Uploader from "../../CustomUploader/Uploader";
import {
  JPEG_PNG_EXTENSIONS,
  JPEG_PNG_MIME_TYPES,
} from "../../CustomUploader/imageUploadConstants";
import AddressAutocompleteRHF from "../../AutoComplete/AddressAutocompleteRHF";
import ActionMenu from "../../actionMenus/action-menu";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  OrganizationService,
  OrganizationVM,
} from "@propertelligent/client-api";
import { UserLoginData } from "../../../components/common/charts/Enums";
import { GetImages } from "../../../utils/GetImages";
import PhoneInput from "../../form-fields/PhoneInput";
import EmailInput from "../../form-fields/EmailInput";
import { usePermissions } from "../../../hooks/usePermissions";
import { NotificationType, PubSub, TopicsEnum } from "../../../utils/pubsub";
import { useError } from "../../../utils/context/ErrorContext";
import { useFieldChangesSnapshot } from "../../../hooks/useFieldChangesSnapshot";
import UtilHelper from "../../../utils/UtilHelper";

interface OrganizationListingCardItemProps {
  listingData: OrganizationVM;
  className?: string;
  onUpdate?: () => void; // Callback to refresh organization data
}

const normalizePhoneForCompare = (value?: string | null) =>
  UtilHelper.normalizePhoneDigits(value);

const OrganizationListingCardItem = ({
  listingData,
  className,
  onUpdate,
}: OrganizationListingCardItemProps) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [documentId, setDocumentId] = useState<number | undefined>(
    listingData.logoImageId,
  );
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [resetCounter, setResetCounter] = useState(0);
  const [imageChanged, setImageChanged] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const originalPhoneRef = useRef<string>("");

  const {
    handleSubmit,
    control,
    formState: { isDirty, isSubmitting, isValid, dirtyFields },
    reset,
    clearErrors,
    getValues,
  } = useFormContext<OrganizationVM & { mainImage?: File | null }>();
  const organizationId = Number(
    sessionStorage.getItem(UserLoginData.organization_id),
  );
  const check = usePermissions();
  const canWrite = check.canWrite("OrganizationDashboard");
  const { clearErrors: clearContextErrors } = useError();

  const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<{
    name?: string;
    email?: string;
    website?: string;
    unitNumber?: string;
    "address.streetAddress"?: string;
    "address.city"?: string;
    "address.state"?: string;
    "address.zip"?: string;
  }>(control, [
    "name",
    "email",
    "website",
    "unitNumber",
    "address.streetAddress",
    "address.city",
    "address.state",
    "address.zip",
  ]);

  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const phoneNumberChanged =
    normalizePhoneForCompare(watchedPhoneNumber) !==
    normalizePhoneForCompare(originalPhoneRef.current);

  // imageChanged sits outside isDirty: a logo File-to-File swap (replace, or delete then upload)
  // never makes the form dirty.
  const hasUnsavedChanges =
    imageChanged ||
    (isDirty &&
      (hasFieldChanges || phoneNumberChanged || Boolean(dirtyFields.mainImage)));

  const buildResetValues = (logoFile: File | null = null) => {
    const rawPhone =
      (listingData as any)?.formatted?.PhoneNumber ||
      listingData.phoneNumber ||
      "";
    const phoneForInput = UtilHelper.toPhoneInputValue(rawPhone);
    return {
      ...listingData,
      phoneNumber: phoneForInput,
      mainImage: logoFile as any,
    };
  };

  const openModal = async () => {
    setImageChanged(false);
    const baseValues = buildResetValues(null);
    originalPhoneRef.current = baseValues.phoneNumber ?? "";
    setSnapshot({
      name: baseValues.name || "",
      email: baseValues.email || "",
      website: baseValues.website || "",
      unitNumber: baseValues.unitNumber || "",
      "address.streetAddress": baseValues.address?.streetAddress || "",
      "address.city": baseValues.address?.city || "",
      "address.state": baseValues.address?.state || "",
      "address.zip": baseValues.address?.zip || "",
    });
    reset(baseValues);

    setDocumentId(listingData.logoImageId || undefined);

    // Agar logo hai to usko form field mein daal do
    if (listingData?.logoImageId && mainImage) {
      // Convert current displayed base64 image into File object
      try {
        const response = await fetch(mainImage);
        const blob = await response.blob();
        const fileName = "organization-logo.png";
        const file = new File([blob], fileName, { type: "image/png" });

        // Yeh line sabse important hai!
        reset(buildResetValues(file));
      } catch (err) {
        console.error("Failed to convert base64 to File for form", err);
      }
    }

    setModalOpen(true);
  };

  useEffect(() => {
    const fetchImage = async () => {
      if (!listingData?.logoImageId) {
        setMainImage(null);
        return;
      }

      setIsLoadingImage(true);
      try {
        const response = await apiHelper(
          DocumentService.getApiDocumentBase641,
          { showNotification: false },
          listingData.logoImageId,
        );

        if (response?.data?.success) {
          const base64 = response.data.success;
          const imageUrl = `data:image/png;base64,${base64}`;
          setMainImage(imageUrl);

          // Agar modal open hai to form mein bhi set kar do
          if (isModalOpen) {
            const res = await fetch(imageUrl);
            const blob = await res.blob();
            const file = new File([blob], "logo.png", { type: "image/png" });
            reset((prev) => ({
              ...prev,
              mainImage: file,
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching logo:", err);
        setMainImage(null);
      } finally {
        setIsLoadingImage(false);
      }
    };

    fetchImage();
  }, [listingData?.logoImageId, isModalOpen]); // ← isModalOpen add kiya

  // Upload image to server
  const uploadImage = async (doc: any) => {
    const docData = {
      EntityTypeId: doc.EntityTypeId,
      EntityId: doc.EntityId,
      DocumentTypeId: doc.DocumentTypeId,
      IsMain: doc.IsMain,
      FileName: doc.FileName,
      Document: doc.Document,
    };

    try {
      // Suppress the generic toast here (no successMessage would otherwise fall back to
      // apiHelper's literal "Api completed successfully" string, firing before the real
      // "Organization updated successfully" toast below). The caller's own
      // `if (!newDocId)` branch already publishes an explicit failure notification, so no
      // failure signal is lost. Same defect class fixed the same way in
      // MoveInAndOutContentBlocks.tsx / AddUnitForm.tsx.
      const res = await apiHelper(
        DocumentService.postApiDocument,
        {
          showNotification: false,
        },
        docData,
      );
      if (res?.data?.success?.id) {
        setDocumentId(res.data.success.id);
        return res.data.success.id;
      }
    } catch (error) {
      console.error("Error uploading document:", JSON.stringify(error));
    }
    return null;
  };

  // Handle form submission
  const onSubmit = async (values: OrganizationVM) => {
    try {
      let updatedDocumentId = documentId;

      // Get mainImage from form values
      const formValues = getValues();
      const imageFile = (formValues as any).mainImage;

      // Check if image was deleted (imageFile is null but we had a documentId)
      if (!imageFile && documentId) {
        // Delete the existing image
        try {
          await apiHelper(
            DocumentService.deleteApiDocument,
            { showNotification: false },
            documentId,
          );
          updatedDocumentId = undefined;
          setDocumentId(undefined);
        } catch (error) {
          console.error("Error deleting document:", error);
        }
      }
      // Upload new image if mainImage exists and is a File object
      else if (imageFile && imageFile instanceof File) {
        const imgData = {
          EntityTypeId: DocumentEntityTypesEnum.Organization,
          EntityId: organizationId,
          DocumentTypeId: DocumentTypesEnum.Logo,
          IsMain: true,
          FileName: imageFile.name,
          Document: imageFile,
        };
        // Upload first so a failed type/validation never leaves the org without a logo
        const newDocId = await uploadImage(imgData);
        if (!newDocId) {
          PubSub.publish(TopicsEnum.Notification.toString(), {
            message: "Unable to upload organization image. Changes were not saved.",
            type: NotificationType.Failure,
          });
          return;
        }

        if (documentId && documentId !== newDocId) {
          try {
            await apiHelper(
              DocumentService.deleteApiDocument,
              { showNotification: false },
              documentId,
            );
          } catch (error) {
            console.error("Error deleting old document:", error);
          }
        }
        updatedDocumentId = newDocId;
      }

      const payload = {
        ...values,
        id: organizationId,
        logoImageId: updatedDocumentId,
      };

      const res = await apiHelper(
        OrganizationService.putApiOrganization,
        {
          title: "Organization Updated",
          successMessage: "Organization updated successfully",
          failureMessage: "Unable to Update Organization",
          showNotification: true,
          successIcon: (
            <img
              src={GetImages.toastPaymentAccountIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
        },
        payload,
      );

      if (res?.data?.success) {
        setModalOpen(false);
        setImageChanged(false);
        setResetCounter((prev) => prev + 1);

        reset({
          ...listingData, // ya phir onUpdate ke baad jo latest data aaya ho
          mainImage: null,
        });

        // Call the onUpdate callback to refresh organization data
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error) {
      console.error("Error updating organization:", error);
    }
  };

  const onCloseModal = () => {
    setImageChanged(false);
    setResetCounter((prev) => prev + 1);
    setModalOpen(false);
    reset(
      {
        ...listingData,
        mainImage: null as any,
      },
      { keepErrors: false, keepDirty: false, keepTouched: false },
    );
    clearErrors();
    clearContextErrors();
  };

  const menuItems = [
    {
      icon: editIcon,
      label: "Edit",
      action: () => openModal(),
      isDivider: false,
      isDestructive: false,
    },
  ];

  return (
    <>
      <div className={`listing-card ${className}`}>
        <div className="listing-card-header">
          <div className="listing-image-container">
            <img
              src={mainImage || GetImages.HomeBlackFilled}
              alt="Listing"
              width={56}
              height={56}
              className={`listing-image ${mainImage ? '' : 'color-white-icon'}`}
            />
          </div>
          <div className="listing-details">
            <div className="listing-info-container">
              <div className="listing-info">
                <h3 className="listing-address">{listingData?.name}</h3>
                <p className="listing-type">
                  {listingData?.address?.streetAddress}
                </p>
                <p className="listing-type">
                  {[listingData?.email, listingData?.formatted?.PhoneNumber]
                    .filter(Boolean)
                    .join(" • ")}
                </p>
              </div>
            </div>
          </div>
          {canWrite && (
            <ActionMenu
              trigger={
                <img src={dropdownIcon} alt="Menu" width={24} height={24} className='iconStyleWithWhiteFilter' />
              }
              menuItems={menuItems}
              Id={organizationId}
            />
          )}
        </div>
      </div>

      <RightSideModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        title={listingData ? "Edit Organization" : "Add Organization"}
        buttonlabel={"Save"}
        formId="organization-form"
        maxWidth={550}
        isDirty={hasUnsavedChanges}
        isLoading={isSubmitting}
        isSubmitDisabled={isSubmitting || !hasUnsavedChanges || !isValid}
      >
        <Card className="no-bg">
          <FormProvider {...useFormContext()}>
            <form id="organization-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-md-12">
                  <Uploader
                    name="mainImage"
                    required={false}
                    requiredMessage="Image is required"
                    allowedFormats={[...JPEG_PNG_MIME_TYPES]}
                    allowedExtensions={[...JPEG_PNG_EXTENSIONS]}
                    defaultImage={mainImage}
                    maxSizeMB={1}
                    resetTrigger={resetCounter}
                    onUserChange={() => setImageChanged(true)}
                  />
                </div>
                <div className="col-md-12">
                  <hr />
                </div>
                <div className="col-md-12">
                  <InputField
                    name="name"
                    label="Organization Name"
                    required
                    requiredMessage="Organization name is required"
                    type="text"
                    placeholder="Enter Organization Name"
                    validation={{
                      minLength: {
                        value: 1,
                        message: "Organization name is required",
                      },
                      maxLength: {
                        value: 20,
                        message: "Organization name cannot exceed 20 characters",
                      },
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <EmailInput
                    name="email"
                    label="Email Address"
                    required
                    requiredMessage="Email is required"
                    placeholder="Enter Email Address"
                  />
                </div>
                <div className="col-md-6">
                  <PhoneInput
                    key={`phone-${resetCounter}`}
                    name="phoneNumber"
                    label="Contact No"
                    required
                    requiredMessage="Contact number is required"
                  />
                </div>
                <div className="col-md-12">
                  <InputField
                    name="address.streetAddress"
                    label="Street Address"
                    required
                    placeholder="Enter Address"
                    requiredMessage="Street address is required"
                    validation={{
                      maxLength: {
                        value: 100,
                        message: "Street Address cannot exceed 100 characters",
                      },
                    }}
                  />
                </div>
                <div className="col-md-12">
                  <InputField
                    name="unitNumber"
                    label="Suite no."
                    type="text"
                    placeholder="Enter Suite no."
                    allowSpecialChars={false}
                    validation={{
                      maxLength: {
                        value: 50,
                        message: "Suite no. cannot exceed 50 characters",
                      },
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <InputField
                    name="address.city"
                    label="City"
                    required
                    requiredMessage="City is required"
                    placeholder="Enter City"
                    onlyAlphabets
                    validation={{
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: "City must contain only letters",
                      },
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <InputField
                    name="address.state"
                    label="State"
                    required
                    requiredMessage="State is required"
                    placeholder="Enter State"
                    validation={{
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: "State must contain only letters",
                      },
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <InputField
                    name="address.zip"
                    label="Zip Code"
                    required
                    requiredMessage="Zip Code is required"
                    placeholder="Enter Zip Code"
                    validation={{
                      pattern: {
                        value: /^\d{5}$/,
                        message: "Zip code must be 5 digits",
                      },
                    }}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </Card>
      </RightSideModal>
    </>
  );
};

export default OrganizationListingCardItem;
